import { checkCDN } from 'shared/lib/checkCDN'
import { delay } from 'shared/lib/delay'

export type IMediaSize = {
  width: number
  height: number
}

const HEADER_BYTES = 4096
const LOAD_TIMEOUT = 5000

const cache = new Map<string, Promise<IMediaSize | null>>()

const readImageSource = (source: string) =>
  new Promise<IMediaSize | null>((resolve) => {
    const image = new Image()

    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => resolve(null)

    if (checkCDN(source)) image.crossOrigin = 'use-credentials'
    image.src = source
  })

const readImageBlob = (blob: Blob) =>
  new Promise<IMediaSize | null>((resolve) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    const done = (size: IMediaSize | null) => {
      URL.revokeObjectURL(url)
      resolve(size)
    }

    image.onload = () => done({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => done(null)
    image.src = url
  })

const readImageHeader = async (source: string) => {
  try {
    const response = await fetch(source, {
      credentials: checkCDN(source) ? 'include' : 'omit',
      headers: { Range: `bytes=0-${HEADER_BYTES - 1}` },
      signal: AbortSignal.timeout(LOAD_TIMEOUT),
    })

    if (!response.ok) return null

    return await readImageBlob(await response.blob())
  } catch {
    return null
  }
}

const readVideo = (source: string) =>
  new Promise<IMediaSize | null>((resolve) => {
    const video = document.createElement('video')

    video.onloadedmetadata = () => resolve({ width: video.videoWidth, height: video.videoHeight })
    video.onerror = () => resolve(null)

    if (checkCDN(source)) video.crossOrigin = 'use-credentials'
    video.preload = 'metadata'
    video.src = source
  })

const read = async (source: string, isVideo: boolean) => {
  if (isVideo) return readVideo(source)

  return (await readImageHeader(source)) ?? readImageSource(source)
}

export const getMediaSize = (source: string, isVideo = false) => {
  const cached = cache.get(source)

  if (cached) return cached

  const stalled = delay(LOAD_TIMEOUT).then(() => null)
  const pending = Promise.race([read(source, isVideo), stalled])

  cache.set(source, pending)

  return pending
}
